import { useEffect, useState } from "react";
import { Plus, Pencil, Trash, FloppyDisk, X, CaretDown, CaretRight, Books } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const blankModule = () => ({ name: "", hours: "", description: "" });

export default function TrainingPrograms() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/training-programs");
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ name: "", default_hours: "", modules: [] });
  const startEdit = (it) => setEditing({
    ...it,
    default_hours: it.default_hours ?? "",
    modules: (it.modules || []).map((m) => ({
      name: m.name || "",
      hours: String(m.hours ?? ""),
      description: m.description || "",
    })),
  });
  const cancel = () => setEditing(null);

  const addModule = () =>
    setEditing((e) => ({ ...e, modules: [...(e.modules || []), blankModule()] }));
  const updateModule = (i, k, v) =>
    setEditing((e) => ({
      ...e,
      modules: e.modules.map((m, idx) => (idx === i ? { ...m, [k]: v } : m)),
    }));
  const removeModule = (i) =>
    setEditing((e) => ({ ...e, modules: e.modules.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!editing.name?.trim()) {
      toast.error("Program adı boş olamaz.");
      return;
    }
    const cleanModules = (editing.modules || [])
      .filter((m) => m.name.trim() && Number(m.hours) > 0)
      .map((m) => ({
        name: m.name.trim(),
        hours: Number(m.hours),
        description: m.description?.trim() || null,
      }));
    setSaving(true);
    try {
      const payload = {
        name: editing.name.trim(),
        default_hours:
          editing.default_hours === "" || editing.default_hours == null
            ? null
            : Number(editing.default_hours),
        modules: cleanModules,
      };
      if (editing.id) {
        await api.put(`/training-programs/${editing.id}`, payload);
        toast.success("Eğitim programı güncellendi.");
      } else {
        await api.post("/training-programs", payload);
        toast.success("Eğitim programı eklendi.");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Kaydedilemedi.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(`"${it.name}" silinsin mi? Bu programa atanmış sertifikalar etkilenmez.`)) return;
    try {
      await api.delete(`/training-programs/${it.id}`);
      toast.success("Silindi.");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Silinemedi.");
    }
  };

  const totalForEditing = (editing?.modules || []).reduce(
    (s, m) => s + (Number(m.hours) || 0),
    0
  );

  return (
    <div className="p-6 lg:p-10" data-testid="programs-page">
      <div className="flex items-end justify-between gap-6 flex-wrap">
        <div>
          <span className="label-eyebrow">/ Eğitim Programları</span>
          <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter text-slate-900 mt-3">
            Müfredat kataloğu
          </h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Her programın <b>tam müfredatını</b> (modüller + saatler + açıklamalar) buradan tanımlayın.
            Sertifika oluştururken program seçildiğinde modüller <b>otomatik dolar ve kilitlenir</b> —
            tutarlılığı garanti eder. Açıklamalar <b>Detaylı Transcript</b> PDF'inde görünür.
          </p>
        </div>
        <button
          onClick={startNew}
          data-testid="program-add-button"
          className="inline-flex items-center gap-2 px-5 py-3 bg-[#0B2046] text-white hover:bg-[#113066] font-medium"
        >
          <Plus size={16} weight="bold" /> Yeni Program
        </button>
      </div>

      {editing && (
        <div className="mt-8 max-w-5xl bg-white border border-slate-200 p-6">
          <div className="label-eyebrow mb-4">{editing.id ? "Programı Düzenle" : "Yeni Program"}</div>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <label className="label-eyebrow block mb-2">Program adı *</label>
              <input
                data-testid="program-form-name"
                autoFocus
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                placeholder="örn. International Cabin Crew Training Program (80h)"
                className="w-full px-4 py-3 border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0B2046]"
              />
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Toplam saat</label>
              <input
                data-testid="program-form-hours"
                type="number"
                step="0.5"
                value={editing.default_hours}
                onChange={(e) => setEditing({ ...editing, default_hours: e.target.value })}
                placeholder="modüllerden otomatik"
                className="w-full px-4 py-3 border border-slate-300 bg-white text-sm font-mono-plex focus:outline-none focus:ring-2 focus:ring-[#0B2046]"
              />
              <p className="text-xs text-slate-500 mt-1">Boş ise modüllerden hesaplanır.</p>
            </div>
          </div>

          {/* Curriculum editor */}
          <div className="mt-6 border-t border-slate-200 pt-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="label-eyebrow flex items-center gap-2"><Books size={14} /> Müfredat</div>
                <p className="text-xs text-slate-500 mt-1">
                  Her modülün adı, saati ve açıklamasını girin. Açıklamalar Detaylı Transcript'te modülün altında görünür.
                </p>
              </div>
              <button
                type="button"
                onClick={addModule}
                data-testid="program-add-module"
                className="text-sm px-3 py-1.5 border border-slate-300 hover:bg-slate-50"
              >
                + Modül ekle
              </button>
            </div>

            {(editing.modules || []).length === 0 && (
              <div className="text-xs text-slate-400 italic">
                Henüz modül yok. "Modül ekle" ile başlayın — bu listeyi tanımlamadığınız programlar dropdown'da görünmeye devam eder, ancak sertifika oluştururken manuel modül girilmesi gerekir.
              </div>
            )}

            <div className="space-y-3">
              {(editing.modules || []).map((m, i) => (
                <div key={i} className="bg-slate-50 border border-slate-200 p-3 rounded-sm" data-testid={`program-module-row-${i}`}>
                  <div className="grid grid-cols-12 gap-2 items-start">
                    <div className="col-span-7">
                      <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Modül adı *</label>
                      <input
                        placeholder="örn. Safety and Emergency Procedures (SEP)"
                        value={m.name}
                        onChange={(e) => updateModule(i, "name", e.target.value)}
                        data-testid={`program-module-name-${i}`}
                        className="w-full px-3 py-2 border border-slate-300 text-sm bg-white"
                      />
                    </div>
                    <div className="col-span-3">
                      <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">Saat *</label>
                      <input
                        type="number"
                        step="0.5"
                        placeholder="örn. 16"
                        value={m.hours}
                        onChange={(e) => updateModule(i, "hours", e.target.value)}
                        data-testid={`program-module-hours-${i}`}
                        className="w-full px-3 py-2 border border-slate-300 text-sm bg-white font-mono-plex"
                      />
                    </div>
                    <div className="col-span-2 flex justify-end pt-5">
                      <button
                        type="button"
                        onClick={() => removeModule(i)}
                        className="px-3 py-2 text-red-600 hover:bg-red-50 text-sm"
                        data-testid={`program-module-remove-${i}`}
                      >
                        ✕ Kaldır
                      </button>
                    </div>
                    <div className="col-span-12">
                      <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">
                        Açıklama (Detaylı Transcript'te modülün altında çıkar)
                      </label>
                      <textarea
                        placeholder="Modülün içeriği — örn. Emergency landing, evacuation, cabin depressurization, firefighting, safety equipment locations."
                        value={m.description}
                        onChange={(e) => updateModule(i, "description", e.target.value)}
                        rows={2}
                        data-testid={`program-module-description-${i}`}
                        className="w-full px-3 py-2 border border-slate-300 text-sm bg-white"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {(editing.modules || []).length > 0 && (
              <div className="mt-3 text-xs font-mono-plex text-slate-700 text-right">
                Modüllerden hesaplanan toplam: <b>{totalForEditing.toFixed(1)} saat</b>
              </div>
            )}
          </div>

          <div className="mt-6 flex items-center justify-end gap-2 border-t border-slate-200 pt-5">
            <button onClick={cancel} className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 inline-flex items-center gap-2">
              <X size={14} /> Vazgeç
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="program-form-save"
              className="px-5 py-2 bg-[#0B2046] text-white hover:bg-[#113066] font-medium text-sm inline-flex items-center gap-2 disabled:opacity-60"
            >
              <FloppyDisk size={14} weight="bold" /> {saving ? "Kaydediliyor…" : "Kaydet"}
            </button>
          </div>
        </div>
      )}

      <div className="mt-8 border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="px-3 py-3 w-8"></th>
              <th className="px-4 py-3 text-left label-eyebrow">Program Adı</th>
              <th className="px-4 py-3 text-left label-eyebrow">Modül Sayısı</th>
              <th className="px-4 py-3 text-left label-eyebrow">Toplam Saat</th>
              <th className="px-4 py-3 text-left label-eyebrow">Eklenme</th>
              <th className="px-4 py-3 text-right label-eyebrow">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={6} className="px-4 py-8 text-center label-eyebrow">Yükleniyor…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-12 text-center">
                  <div className="label-eyebrow">Henüz eğitim programı yok</div>
                  <button onClick={startNew} className="mt-3 text-sm font-medium text-[#0B2046] underline underline-offset-4">
                    İlk programı ekle →
                  </button>
                </td>
              </tr>
            )}
            {items.map((it) => {
              const modCount = (it.modules || []).length;
              const isOpen = expanded[it.id];
              return (
                <ProgramRow
                  key={it.id}
                  it={it}
                  modCount={modCount}
                  isOpen={isOpen}
                  onToggle={() => setExpanded((e) => ({ ...e, [it.id]: !e[it.id] }))}
                  onEdit={() => startEdit(it)}
                  onDelete={() => remove(it)}
                />
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProgramRow({ it, modCount, isOpen, onToggle, onEdit, onDelete }) {
  return (
    <>
      <tr className="border-t border-slate-200 hover:bg-slate-50" data-testid={`program-row-${it.id}`}>
        <td className="px-3 py-3">
          {modCount > 0 && (
            <button onClick={onToggle} className="text-slate-500 hover:text-slate-900" data-testid={`program-expand-${it.id}`}>
              {isOpen ? <CaretDown size={14} /> : <CaretRight size={14} />}
            </button>
          )}
        </td>
        <td className="px-4 py-3 font-medium">{it.name}</td>
        <td className="px-4 py-3 font-mono-plex">
          {modCount > 0 ? <span className="text-emerald-700 font-bold">{modCount}</span> : <span className="text-slate-400">— manuel</span>}
        </td>
        <td className="px-4 py-3 font-mono-plex">
          {it.default_hours != null ? `${it.default_hours} saat` : <span className="text-slate-400">—</span>}
        </td>
        <td className="px-4 py-3 font-mono-plex text-xs text-slate-500">
          {it.created_at ? new Date(it.created_at).toLocaleDateString("tr-TR") : "-"}
        </td>
        <td className="px-4 py-3 text-right whitespace-nowrap">
          <button onClick={onEdit} data-testid={`program-edit-${it.id}`}
            className="inline-flex items-center gap-1 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100">
            <Pencil size={14} /> Düzenle
          </button>
          <button onClick={onDelete} data-testid={`program-delete-${it.id}`}
            className="ml-1 inline-flex items-center gap-1 px-2 py-1 text-xs text-red-700 hover:bg-red-50">
            <Trash size={14} /> Sil
          </button>
        </td>
      </tr>
      {isOpen && modCount > 0 && (
        <tr className="bg-slate-50/50">
          <td colSpan={6} className="px-12 py-4">
            <div className="text-xs label-eyebrow mb-3">Müfredat (Detaylı Transcript'te görünür)</div>
            <ul className="space-y-2">
              {it.modules.map((m, i) => (
                <li key={i} className="bg-white border border-slate-200 p-3 text-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div className="font-medium text-slate-900">{i + 1}. {m.name}</div>
                    <div className="font-mono-plex text-xs text-slate-600 whitespace-nowrap">{m.hours} saat</div>
                  </div>
                  {m.description && (
                    <div className="mt-1.5 text-xs text-slate-600 italic leading-relaxed">{m.description}</div>
                  )}
                </li>
              ))}
            </ul>
          </td>
        </tr>
      )}
    </>
  );
}
